// ============================================================================
// Shared major-relation vocabulary — the ONE place the relation colors, the
// badge copy, the selection cap and the availability wording live.
//
// Extracted from MajorMapCard (#1187) so surfaces outside the Major Map page
// (the Concierge chat's in-thread picker) can speak the same visual language
// without importing the page card and, with it, credits/analytics/physics.
// MajorMapCard re-exports everything here, so its existing importers are
// unchanged.
// ============================================================================

// How widely a major is actually offered, from the catalogue grounding pass.
// The denominator matters: a bare "offered at 3 colleges" reads as "only 3 in
// America", when it means 3 of the schools in Stratia's knowledge base. Below
// RARE_AT, the scarcity is the point — it quietly constrains where a student
// can apply, so it is called out rather than shown as just another number.
export const RARE_AT = 10;

export function offeredLabel(count, total) {
    if (typeof count !== 'number' || count <= 0) return null;
    const of = typeof total === 'number' && total > 0 ? ` of ${total}` : '';
    return {
        text: `Offered at ${count}${of} colleges we track`,
        rare: count < RARE_AT,
    };
}

// How the availability reads at a glance. Bands are PROPORTIONS, not raw
// counts: 33 of 217 is a real constraint on a college list, 33 of 40 is not.
// Only the bottom band is coloured — amber on half the rows would recreate the
// noise this redesign exists to remove.
export function availabilityBand(count, total) {
    if (typeof count !== 'number' || count <= 0) return null;
    const pct = typeof total === 'number' && total > 0 ? count / total : null;
    if (pct === null) return { caption: 'colleges we track', tone: 'plain', pct: null };
    // Under a fifth of the colleges we track, the major — not the college —
    // starts deciding where a list can go. That is advice, so it is coloured.
    if (pct >= 0.5) return { caption: 'widely offered', tone: 'plain', pct };
    if (pct >= 0.2) return { caption: 'offered selectively', tone: 'plain', pct };
    return { caption: 'narrows your list', tone: 'scarce', pct };
}

// Core first, then the widest-offered within each band. The counselor's
// narrative order buried the one major most students should look at first.
const RELATION_RANK = { core: 0, adjacent: 1, strategic_alternative: 2 };
export function rankMajors(majors) {
    return [...(majors || [])].sort((a, b) => {
        const ra = RELATION_RANK[a.relation] ?? 1;
        const rb = RELATION_RANK[b.relation] ?? 1;
        if (ra !== rb) return ra - rb;
        return (b.offered_count || 0) - (a.offered_count || 0);
    });
}

export const RELATION_RAIL = {
    core: 'bg-[#1A4D2E]',
    adjacent: 'bg-[#1B4F72]',
    strategic_alternative: 'bg-[#C05838]',
};
export const RELATION_TEXT = {
    core: 'text-[#1A4D2E]',
    adjacent: 'text-[#1B4F72]',
    strategic_alternative: 'text-[#C05838]',
};

export const RELATION_BADGES = {
    core: {
        label: 'Core',
        desc: 'A direct match to your strengths and interests.',
        className: 'bg-green-100 text-green-800 border border-green-200',
    },
    adjacent: {
        label: 'Adjacent',
        desc: 'A closely related field worth exploring.',
        className: 'bg-blue-100 text-blue-800 border border-blue-200',
    },
    strategic_alternative: {
        label: 'Strategic alternative',
        desc: 'A smart alternate route toward the same goal.',
        className: 'bg-[#FCEEE8] text-[#C05838] border border-[#E8A090]',
    },
};

// The declared-majors budget. Every surface that lets a student set majors
// enforces the same cap — the server stores at most this many.
export const MAX_SELECTED = 5;

// Which of `names` this map actually offers, spelled the way the MAP spells
// them, in the order they were asked for. The one intersection: the tray seeds
// from declared majors through it, the anchor is resolved through it, and a
// regenerate re-seeds the surviving picks through it — three places that must
// agree on what "this major is on the map" means, or a selection silently
// half-survives. Matching is case-insensitive because a student's declared
// "computer science" and the catalogue's "Computer Science" are one major.
export function majorsOnMap(map, names) {
    const byKey = new Map();
    for (const cluster of (map?.clusters || [])) {
        for (const major of (cluster?.majors || [])) {
            const name = major?.name;
            if (typeof name === 'string' && name.trim() && !byKey.has(name.trim().toLowerCase())) {
                byKey.set(name.trim().toLowerCase(), name);
            }
        }
    }
    const out = [];
    for (const raw of (names || [])) {
        if (typeof raw !== 'string') continue;
        const hit = byKey.get(raw.trim().toLowerCase());
        if (hit && !out.includes(hit)) out.push(hit);
    }
    return out;
}
